import React, { useContext, useEffect, useMemo, useState } from 'react';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import {
  Box,
  Text,
  VStack,
  Flex,
  Icon,
  AspectRatio,
  Progress,
  useToast,
} from '@chakra-ui/react';
import { FaPlus, FaTimes, FaCheck, FaCamera, FaVideo } from 'react-icons/fa';
import { ContentRequirement, ContentSubmissionUnit } from '@foodfluence/shared';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';
import BottomSheet from './BottomSheet';
import { getGroupedContent } from '../../../utils/groupedContentUtils';
import { getContentTypeCount } from '../../../utils/contentUtils';
import { triggerFileSelection, isVideoFile } from '../../../utils/contentUploadUtils';
import { hasNewContent } from '../../../utils/contentUtils';
import { uploadAndSubmitUgcContent } from '../../../utils/ugcSubmissionUtils';
import { deliverableApi } from '../../../services/api/deliverable';
import { isVideoUrl } from '../../../utils/fileUtils';
import BrandFeedbackBody from './BrandFeedbackBody';
import { SheetButton } from './counter-flow/sheetParts';
import { SubmissionSheetTitle, UploadProgressRow } from './submissionSheetParts';
import { ff } from '../../../theme';

interface Slot {
  key: string;
  unit?: ContentSubmissionUnit;
}

interface MultiContentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  requirement: ContentRequirement;
  requirementId: string;
  contentMap: Record<string, ContentSubmissionUnit>;
  collabId: string;
  clientId: string;
  modalHeader: string;
}

const MultiContentUploadModal: React.FC<MultiContentUploadModalProps> = ({
  isOpen,
  onClose,
  requirement,
  requirementId,
  contentMap,
  collabId,
  clientId,
  modalHeader,
}) => {
  const toast = useToast();
  const requiredCount = getContentTypeCount(requirement);
  const isVideo = requirement.type === 'video';

  const { influencerData } = useContext(InfluencerDataContext);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
  const [existingUrls, setExistingUrls] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  // Stable slot list: existing units keep their real keys; remaining slots get
  // deterministic keys with an underscore so the backend treats them as
  // carousel items copied from the base unit.
  const slots: Slot[] = useMemo(() => {
    const existing = getGroupedContent(contentMap, requirementId);
    const emptyCount = Math.max(0, requiredCount - existing.length);
    const empties: Slot[] = Array.from({ length: emptyCount }, (_, i) => ({
      key: `${requirementId}_new_${existing.length + i}`,
    }));
    return [...existing.map(e => ({ key: e.key, unit: e.unit })), ...empties];
  }, [contentMap, requirementId, requiredCount]);

  // Local object URLs for newly picked files (revoked when they change/unmount).
  const filePreviews = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(uploadedFiles).forEach(([k, f]) => { map[k] = URL.createObjectURL(f); });
    return map;
  }, [uploadedFiles]);

  useEffect(() => () => {
    Object.values(filePreviews).forEach(URL.revokeObjectURL);
  }, [filePreviews]);

  // Resolve download URLs for already-submitted pieces.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const updates: Record<string, string> = {};
      for (const slot of slots) {
        if (slot.unit?.fileUrl && !existingUrls[slot.key]) {
          try {
            updates[slot.key] = await getDownloadURL(ref(storage, slot.unit.fileUrl));
          } catch {
            /* ignore — tile falls back to a placeholder */
          }
        }
      }
      if (!cancelled && Object.keys(updates).length) {
        setExistingUrls(prev => ({ ...prev, ...updates }));
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, slots]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setUploadedFiles({});
    setProgress(0);
    setIsSubmitting(false);
    onClose();
  };

  const pickFile = (key: string) => {
    triggerFileSelection(requirement, (file) => {
      setUploadedFiles(prev => ({ ...prev, [key]: file }));
    });
  };

  const removeFile = (key: string) => {
    setUploadedFiles(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // A slot is ready when it has a freshly picked file, or already-submitted
  // content that the brand hasn't flagged for edits.
  const isSlotReady = (slot: Slot) =>
    Boolean(uploadedFiles[slot.key]) || Boolean(slot.unit?.fileUrl && !slot.unit?.contentRejected);

  const allReady = slots.every(isSlotReady);
  const hasUploads = Object.keys(uploadedFiles).length > 0;

  const handleSubmit = async () => {
    if (!allReady || !hasUploads) {
      toast({
        title: 'More content needed',
        description: `Please add all ${requiredCount} ${isVideo ? 'videos' : 'photos'} before submitting.`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    setProgress(0);

    try {
      const entries = Object.entries(uploadedFiles);
      const perFileProgress = new Array(entries.length).fill(0);
      await Promise.all(entries.map(([contentKey, file], i) =>
        uploadAndSubmitUgcContent(
          { collabId, clientId, contentKey, file, influencerId: influencerData?.id },
          (pct) => {
            perFileProgress[i] = pct;
            setProgress(perFileProgress.reduce((sum, p) => sum + p, 0) / entries.length);
          },
        )
      ));

      if (hasNewContent(contentMap, entries.map(([k]) => k))) {
        deliverableApi.notifyContentReview({ collabId }, influencerData?.id);
      }

      toast({
        title: 'Content submitted!',
        description: `Your ${entries.length} ${entries.length === 1 ? 'file' : 'files'} ${entries.length === 1 ? 'has' : 'have'} been submitted for review.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      handleClose();
    } catch (error: any) {
      console.error('ERROR SUBMITTING UGC CONTENT: ', error);
      let errorMessage = 'There was an error submitting your content.';
      if (error?.message) errorMessage = error.message;
      else if (error?.response?.status === 413) errorMessage = 'File is too large. Please try a smaller file.';
      else if (error?.name === 'AbortError') errorMessage = 'Upload was cancelled. Please try again.';
      else if (error?.message === 'Load failed') errorMessage = 'Upload failed. Please check your connection and try again.';
      toast({ title: 'Submission failed', description: errorMessage, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsSubmitting(false);
      setProgress(0);
    }
  };

  const filledCount = slots.filter(isSlotReady).length;
  const anyRejected = slots.some(s => s.unit?.contentRejected);
  const canSubmit = allReady && hasUploads && !isSubmitting;
  const FallbackIcon = isVideo ? FaVideo : FaCamera;

  const itemLabel = (index: number) => `${isVideo ? 'Video' : 'Photo'} ${index + 1}`;

  const STATUS_STRIP_HEIGHT = { base: '72px', md: '80px' };

  const anyWithStatusStrip = slots.some(({ key, unit }) => {
    const file = uploadedFiles[key];
    return Boolean(unit?.contentRejected) || (unit?.status === 'approved' && !file);
  });

  const renderStatusStrip = (slot: Slot, file?: File) => {
    const showBrandFeedback = Boolean(slot.unit?.contentRejected);
    const isApproved = slot.unit?.status === 'approved' && !file;

    if (!anyWithStatusStrip) return null;

    return (
      <Box h={STATUS_STRIP_HEIGHT} w="full" flexShrink={0}>
        {showBrandFeedback ? (
          <Box
            h="full"
            w="full"
            overflowY="auto"
            bg="#FBE1DC"
            color="#6E1A0A"
            borderRadius="12px"
            px="12px"
            py="8px"
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': { background: '#E8B4AE', borderRadius: 'full' },
            }}
          >
            <Text fontSize="11px" fontWeight={600} color="#6E1A0A" textTransform="uppercase" letterSpacing="0.06em" mb="2px">
              Brand feedback
            </Text>
            <BrandFeedbackBody
              feedback={slot.unit?.feedback ?? ''}
              pdfUrl={slot.unit?.editRequestPdf}
              compact
            />
          </Box>
        ) : isApproved ? (
          <Flex
            h="full"
            w="full"
            align="center"
            justify="center"
            bg="#E4F3CC"
            borderRadius="12px"
            px="12px"
          >
            <Text fontSize="12px" fontWeight={600} color="#2C5208" textTransform="uppercase" letterSpacing="0.06em">
              Approved
            </Text>
          </Flex>
        ) : null}
      </Box>
    );
  };

  const renderTile = (slot: Slot, index: number) => {
    const file = uploadedFiles[slot.key];
    const previewUrl = file ? filePreviews[slot.key] : (slot.unit?.fileUrl ? existingUrls[slot.key] : undefined);
    const showsVideo = file ? isVideoFile(file) : isVideoUrl(slot.unit?.fileUrl ?? '');
    const isApproved = slot.unit?.status === 'approved' && !file;
    const isRejected = slot.unit?.contentRejected && !file;
    const isPending = slot.unit?.status === 'pendingVerification' && !file;
    const filled = Boolean(file || slot.unit?.fileUrl);
    const editable = !isApproved;

    let pill: { label: string; bg: string; color: string } | null = null;
    if (file) pill = { label: 'Ready', bg: ff.ink, color: ff.lime };
    else if (!anyWithStatusStrip && isApproved) pill = { label: 'Approved', bg: '#E4F3CC', color: '#2C5208' };
    else if (!anyWithStatusStrip && isRejected) pill = { label: 'Needs edit', bg: '#FBE1DC', color: '#A1260F' };
    else if (isPending && slot.unit?.fileUrl) pill = { label: 'Under review', bg: '#F0F0EE', color: '#3E3E42' };

    return (
      <Box
        as={editable ? 'button' : 'div'}
        aria-label={editable ? `${filled ? 'Replace' : 'Add'} ${itemLabel(index)}` : undefined}
        borderRadius="14px"
        overflow="hidden"
        position="relative"
        cursor={editable ? 'pointer' : 'default'}
        onClick={editable ? () => pickFile(slot.key) : undefined}
        border={filled ? (isRejected ? '2px solid #A1260F' : '1px solid #E4E4E0') : '1.5px dashed #9A9A9E'}
        bg={filled ? ff.grey : '#F7F7F5'}
        transition="all 0.15s"
        w="full"
        h="full"
      >
          {filled && previewUrl ? (
            showsVideo ? (
              <video src={previewUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted preload="metadata" />
            ) : (
              <img src={previewUrl} alt={`Item ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )
          ) : filled ? (
            // Filled but preview still resolving
            <Flex align="center" justify="center" w="full" h="full" bg={ff.grey}>
              <Icon as={FallbackIcon} boxSize={6} color={ff.muted} />
            </Flex>
          ) : (
            // Empty slot
            <VStack spacing={2} justify="center" w="full" h="full">
              <Flex w="34px" h="34px" borderRadius="full" bg="white" align="center" justify="center" color={ff.ink}>
                <Icon as={FaPlus} boxSize={3.5} />
              </Flex>
              <Text fontSize="13px" fontWeight={600} color={ff.ink}>
                {itemLabel(index)}
              </Text>
            </VStack>
          )}

          {/* Index badge */}
          {filled && (
            <Flex
              position="absolute"
              top="8px"
              left="8px"
              minW="26px"
              h="26px"
              px="6px"
              borderRadius="full"
              bg={ff.ink}
              align="center"
              justify="center"
            >
              <Text fontSize="13px" fontWeight={600} color={ff.lime} lineHeight={1}>
                {index + 1}
              </Text>
            </Flex>
          )}

          {/* Approved check — only when there is no status strip above */}
          {isApproved && !anyWithStatusStrip && (
            <Flex position="absolute" top="8px" right="8px" w="24px" h="24px" borderRadius="full" bg="#E4F3CC" align="center" justify="center">
              <Icon as={FaCheck} boxSize={2.5} color="#2C5208" />
            </Flex>
          )}

          {/* Remove (only for a freshly picked file) */}
          {file && (
            <Flex
              as="span"
              role="button"
              aria-label={`Remove ${itemLabel(index)}`}
              position="absolute"
              top={0}
              right={0}
              w="44px"
              h="44px"
              align="center"
              justify="center"
              cursor="pointer"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); removeFile(slot.key); }}
            >
              <Flex w="24px" h="24px" borderRadius="full" bg="white" color={ff.ink} align="center" justify="center">
                <Icon as={FaTimes} boxSize={2.5} />
              </Flex>
            </Flex>
          )}

          {/* Status pill */}
          {pill && (
            <Flex position="absolute" bottom={2} left={2} right={2} justify="flex-start">
              <Box bg={pill.bg} color={pill.color} px="10px" h="24px" display="flex" alignItems="center" borderRadius="full" fontSize="12px" fontWeight={600}>
                {pill.label}
              </Box>
            </Flex>
          )}
        </Box>
    );
  };

  const renderSlide = (slot: Slot, index: number) => {
    const file = uploadedFiles[slot.key];

    return (
      <Box
        key={slot.key}
        flex="0 0 auto"
        w={{ base: '156px', md: '200px' }}
        scrollSnapAlign="start"
      >
        <VStack align="stretch" spacing={2} w="full">
          <Text fontSize="12px" fontWeight={600} color="#55555A" letterSpacing="0.08em" textTransform="uppercase">
            {itemLabel(index)}
          </Text>

          {renderStatusStrip(slot, file)}

          <AspectRatio ratio={1} w="full" flexShrink={0}>
            {renderTile(slot, index)}
          </AspectRatio>
        </VStack>
      </Box>
    );
  };

  const contentPadding = '20px';

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose}>
      <Box overflowY="auto" w="full" pt={1} pb="28px" fontFamily="display" color={ff.ink}>
        <Box px={contentPadding} w="full">
          <SubmissionSheetTitle
            title={modalHeader}
            context={
              <Flex as="span" justify="space-between" gap="12px">
                <span>{requiredCount} {isVideo ? 'videos' : 'photos'} for this piece</span>
                <Text as="span" color={filledCount >= requiredCount ? '#2C5208' : '#55555A'}>
                  {filledCount} of {requiredCount} added
                </Text>
              </Flex>
            }
          />
        </Box>

        <VStack spacing="16px" align="stretch" w="full">
          <Box px={contentPadding} w="full">
            <Progress
              value={(filledCount / requiredCount) * 100}
              h="6px"
              borderRadius="full"
              bg={ff.line}
              sx={{ '& > div': { background: ff.ink } }}
              aria-label={`${filledCount} of ${requiredCount} added`}
            />
          </Box>

          <Box w="full" overflow="hidden">
            <Flex
              gap={{ base: 3, md: 4 }}
              overflowX="auto"
              pt={2}
              pb={3}
              pl="20px"
              pr="20px"
              align="flex-start"
              scrollSnapType="x mandatory"
              css={{
                scrollPaddingInline: '2rem',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              {slots.map((slot, index) => renderSlide(slot, index))}
            </Flex>
          </Box>

          <Box px={contentPadding} w="full">
            {isSubmitting && (
              <Box mb="12px">
                <UploadProgressRow
                  fileName={`${Object.keys(uploadedFiles).length} ${Object.keys(uploadedFiles).length === 1 ? 'file' : 'files'}`}
                  percent={progress}
                />
              </Box>
            )}
            <SheetButton
              tone="lime"
              onClick={handleSubmit}
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
              w="full"
              fontSize="16px"
            >
              {anyRejected ? 'Submit revision' : 'Submit content'}
            </SheetButton>
          </Box>
        </VStack>
      </Box>
    </BottomSheet>
  );
};

export default MultiContentUploadModal;
